import { FOG_CELLS_PER_CHUNK, OVERVIEW_PICTURE_MOST, cellsAfterChunk, type AssetManifest, type CellRect, type ChunkRef } from "daedalus-core";
import { MapPictureCache, type PictureAtlas, type PictureLayer, type PictureSurface } from "daedalus-renderer-phaser";
import { FOG_CELL_TILES, fogCells, newExploredSet, type ExploredSet } from "odyssey-shared";
import { cellsToRedraw, changedCells, createFogBlur, paintFogCell, paintPlaceholder, type FogBlur, type FogCell, type FogContext } from "./fog.ts";
import { MAP_SCALE } from "./geometry.ts";
import { meanTone, toneColour } from "./tone.ts";

/**
 * The world map's picture (ADR-100 decisions 2 and 3): the whole map drawn into one canvas of 2 pixels a tile, a chunk a
 * step with the chunk under the character first, and over it the fade on each fog cell the character has not explored.
 * Two surfaces: the ground, which the shared picture cache composes chunk by chunk from the atlas pages the page already
 * holds (`MapPictureCache`, the painter the editor's overview shares), and the display, which is the ground with the
 * fade composed on and is what a view draws from. A chunk is drawn once and kept, so a reopen draws nothing new, and a
 * cell that clears redraws that cell and the four beside it. The class holds no game state: it is handed the map's size,
 * a feed that gives the layers of a chunk, the atlas and the surfaces, so the editor's overview can build the same
 * picture from its own layers.
 */

/**
 * The most tiles a map may have along its longer side for the whole of it to be one picture: the shared cache crops a
 * picture past `OVERVIEW_PICTURE_MOST` pixels, and this one is whole or not made.
 */
export const WORLD_MAP_MOST_TILES = Math.floor(OVERVIEW_PICTURE_MOST / MAP_SCALE);

/**
 * A surface the ground is composed on, and what the display draws it from.
 */
export interface GroundSurface<S> extends PictureSurface<S> {
  readonly source: S;
  readonly read?: (x: number, y: number, width: number, height: number) => ArrayLike<number> | null;
}

/**
 * The display: a context to compose on, and what a view draws it from.
 */
export interface DisplaySurface<S> {
  readonly context: FogContext<S>;
  readonly source: S;
  readonly put?: (pixels: Uint8ClampedArray, x: number, y: number, width: number, height: number) => void;
}

/**
 * What the picture is made from.
 */
export interface WorldMapPictureConfig<S> {
  readonly map: { readonly id: string; readonly width: number; readonly height: number };
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly manifest: AssetManifest;
  readonly feed: (cx: number, cy: number) => readonly PictureLayer[];
  readonly atlas: PictureAtlas<S>;
  readonly ground: (width: number, height: number) => GroundSurface<S> | null;
  readonly display: (width: number, height: number) => DisplaySurface<S> | null;
}

/**
 * The picture.
 */
export class WorldMapPicture<S> {
  private readonly cache: MapPictureCache<S>;
  private readonly columns: number;
  private readonly rows: number;
  private readonly chunksWide: number;
  private ground: GroundSurface<S> | null = null;
  private shown: DisplaySurface<S> | null = null;
  private blur: FogBlur | undefined;
  private held: ExploredSet;
  private requested = false;
  private toned = false;
  private fed: ChunkRef | null = null;
  private readonly drawn = new Set<number>();
  private changes = 0;
  private readonly order: ChunkRef[] = [];

  /**
   * @param config The map, the feed, the atlas and the surfaces.
   */
  public constructor(private readonly config: WorldMapPictureConfig<S>) {
    this.columns = fogCells(config.map.width);
    this.rows = fogCells(config.map.height);
    this.chunksWide = Math.ceil(config.map.width / config.chunkSize);
    this.held = newExploredSet(this.columns, this.rows);
    this.cache = new MapPictureCache<S>(
      (width, height) => {
        this.ground = config.ground(width, height);
        return this.ground;
      },
      config.atlas,
      1,
      null,
    );
  }

  /**
   * A number that moves whenever the display's pixels may have changed, so a view draws only when it does.
   *
   * @returns The version.
   */
  public get version(): number {
    return this.changes;
  }

  /**
   * Chunks still to draw.
   *
   * @returns The count, 0 before the picture is started.
   */
  public get pending(): number {
    return this.cache.pending(this.config.map.id, MAP_SCALE);
  }

  /**
   * Chunks drawn over nothing for want of an atlas page, waiting for `retry`.
   *
   * @returns The count.
   */
  public get incomplete(): number {
    return this.cache.incomplete(this.config.map.id, MAP_SCALE);
  }

  /**
   * How many chunks the map has.
   *
   * @returns The count.
   */
  public get chunks(): number {
    return this.chunksWide * Math.ceil(this.config.map.height / this.config.chunkSize);
  }

  /**
   * The chunks drawn so far, in the order they were drawn: the one under the character first.
   *
   * @returns The chunks.
   */
  public get drawnOrder(): readonly ChunkRef[] {
    return this.order;
  }

  /**
   * The surface a view draws from: the ground with the fade on it.
   *
   * @returns The source, or null before the picture is started.
   */
  public get source(): S | null {
    return this.shown?.source ?? null;
  }

  /**
   * The picture's size in pixels.
   *
   * @returns The size.
   */
  public get size(): { readonly width: number; readonly height: number } {
    return { width: this.config.map.width * MAP_SCALE, height: this.config.map.height * MAP_SCALE };
  }

  /**
   * Starts the picture: makes its surfaces, lays the placeholder and queues every chunk, the one under the character
   * and then those nearest it. A second call changes nothing, so each chunk is drawn once.
   *
   * @param from The chunk under the character.
   * @returns False when the browser gives no canvas or the map is longer than `WORLD_MAP_MOST_TILES`.
   */
  public start(from: ChunkRef): boolean {
    if (this.requested) {
      return this.ground !== null && this.shown !== null;
    }
    this.requested = true;
    if (this.config.map.width > WORLD_MAP_MOST_TILES || this.config.map.height > WORLD_MAP_MOST_TILES) {
      return false;
    }
    const { width, height } = this.size;
    const shown = this.config.display(width, height);
    if (shown === null) {
      return false;
    }
    this.shown = shown;
    const put = shown.put;
    this.blur = put === undefined ? undefined : createFogBlur((x, y, w, h) => this.ground?.read?.(x, y, w, h) ?? null, put);
    const config = this.config;
    const picture = this.cache.request(
      {
        map: config.map.id,
        scale: MAP_SCALE,
        revision: 1,
        width: config.map.width,
        height: config.map.height,
        chunkSize: config.chunkSize,
        tileSize: config.tileSize,
        manifest: config.manifest,
        feed: (cx, cy) => {
          this.fed = { cx, cy };
          return config.feed(cx, cy);
        },
      },
      from,
    );
    if (picture === null) {
      return false;
    }
    paintPlaceholder(shown.context, width, height);
    this.changes += 1;
    return true;
  }

  /**
   * Draws the next chunk and composes the fog cells over it.
   *
   * @returns How many chunks were drawn, 0 or 1.
   * @throws Error whatever the chunk's feed or paint throws; the chunk is then left for `retry`.
   */
  public step(): number {
    if (this.ground === null || this.shown === null) {
      return 0;
    }
    this.fed = null;
    let composed = 0;
    try {
      composed = this.cache.step(this.config.map.id, MAP_SCALE);
    } finally {
      const chunk = this.fed as ChunkRef | null;
      if (chunk !== null) {
        this.drawn.add(chunk.cy * this.chunksWide + chunk.cx);
        if (!this.toned && this.layTone(chunk)) {
          this.toned = true;
          for (const earlier of this.order) {
            this.composeChunk(earlier);
          }
        }
        this.order.push(chunk);
        this.composeChunk(chunk);
        this.composeRing(chunk);
        this.changes += 1;
      }
    }
    return composed;
  }

  /**
   * Queues the chunks drawn without an atlas page again, for when the page has loaded.
   *
   * @returns How many were queued.
   */
  public retry(): number {
    return this.cache.retryIncomplete(this.config.map.id, MAP_SCALE);
  }

  /**
   * Takes the exploration the page holds. A set of other counts than the map's is drawn as nothing explored. Only the
   * cells that differ from the last set, and their neighbours, are composed again.
   *
   * @param set The set, or null for none.
   */
  public setExplored(set: ExploredSet | null): void {
    const next = set !== null && set.columns === this.columns && set.rows === this.rows ? set : newExploredSet(this.columns, this.rows);
    const changed = changedCells(this.held, next);
    this.held = next;
    this.compose(cellsToRedraw(changed, this.columns, this.rows));
  }

  /**
   * Tells the picture cells were set in the set it holds, since the page extends its own copy in place as the
   * character walks.
   *
   * @param cleared The cells that were set.
   */
  public explore(cleared: readonly FogCell[]): void {
    this.compose(cellsToRedraw(cleared, this.columns, this.rows));
  }

  /**
   * Draws again the chunks that cells touch, for shrine art that arrived or left (ADR-100 decision 58): each is queued
   * and drawn by `step` as the others are, then composed under the fade again. Nothing is queued before the picture is
   * started, which reads the art when it draws.
   *
   * @param rects The cells, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
   * @returns How many chunks were queued, at most those the rectangles touch.
   */
  public redraw(rects: readonly CellRect[]): number {
    let queued = 0;
    for (const rect of rects) {
      queued += this.cache.markDirty(this.config.map.id, rect);
    }
    return queued;
  }

  /**
   * Frees the surfaces and forgets what was drawn.
   */
  public dispose(): void {
    this.cache.clear();
    this.ground = null;
    this.shown = null;
    this.blur = undefined;
    this.drawn.clear();
    this.order.length = 0;
    this.requested = false;
    this.toned = false;
  }

  /**
   * Lays the ground's mean tone over the whole display in place of the placeholder (P2.12-T32), read from the first
   * chunk drawn that has ground, so ground not drawn yet shows the colour of the ground and never ink or grey. A ground that cannot be
   * read, or has no opaque pixel there, leaves the placeholder as it is, and the next chunk drawn is tried again.
   *
   * @param chunk A chunk just drawn.
   * @returns True when the tone was laid.
   */
  private layTone(chunk: ChunkRef): boolean {
    const shown = this.shown;
    const read = this.ground?.read;
    if (shown === null || read === undefined) {
      return false;
    }
    const span = this.config.chunkSize * MAP_SCALE;
    const x = chunk.cx * span;
    const y = chunk.cy * span;
    const { width, height } = this.size;
    const pixels = read(x, y, Math.min(span, width - x), Math.min(span, height - y));
    const tone = pixels === null ? null : meanTone(pixels);
    if (tone !== null) {
      paintPlaceholder(shown.context, width, height, MAP_SCALE, toneColour(tone));
      return true;
    }
    return false;
  }

  /**
   * Composes every cell a chunk covers.
   *
   * @param chunk The chunk just drawn.
   */
  private composeChunk(chunk: ChunkRef): void {
    const side = FOG_CELL_TILES * MAP_SCALE;
    const span = this.config.chunkSize * MAP_SCALE;
    const x0 = chunk.cx * span;
    const y0 = chunk.cy * span;
    const cells: FogCell[] = [];
    for (let row = Math.floor(y0 / side); row <= Math.floor((y0 + span - 1) / side); row += 1) {
      for (let column = Math.floor(x0 / side); column <= Math.floor((x0 + span - 1) / side); column += 1) {
        cells.push([column, row]);
      }
    }
    this.paint(cells);
  }

  /**
   * Composes again the cells one step around a chunk that has just drawn, over chunks already drawn, so a cell whose
   * blur was read before this chunk existed is blurred again with the real pixels and no seam of placeholder is left
   * (ADR-100 decision 63). A chunk of the standard four cells a side is listed by `cellsAfterChunk`; any other chunk
   * size lists its own cells grown by one.
   *
   * @param chunk The chunk just drawn.
   */
  private composeRing(chunk: ChunkRef): void {
    if (this.blur === undefined) {
      return;
    }
    const across = this.config.chunkSize / FOG_CELL_TILES;
    const cells: FogCell[] = [];
    if (across === FOG_CELLS_PER_CHUNK) {
      for (const index of cellsAfterChunk({ column: chunk.cx, row: chunk.cy }, this.columns, this.rows)) {
        cells.push([index % this.columns, Math.floor(index / this.columns)]);
      }
    } else {
      const left = Math.max(0, Math.floor(chunk.cx * across) - 1);
      const top = Math.max(0, Math.floor(chunk.cy * across) - 1);
      const right = Math.min(this.columns, Math.ceil((chunk.cx + 1) * across) + 1);
      const bottom = Math.min(this.rows, Math.ceil((chunk.cy + 1) * across) + 1);
      for (let row = top; row < bottom; row += 1) {
        for (let column = left; column < right; column += 1) {
          cells.push([column, row]);
        }
      }
    }
    this.compose(cells.filter(([column, row]) => !this.isOwn(chunk, column, row)));
  }

  /**
   * Whether a cell begins inside the chunk, so `composeChunk` has just composed it.
   *
   * @param chunk The chunk.
   * @param column The cell's column.
   * @param row The cell's row.
   * @returns True when the cell's top left corner is inside the chunk.
   */
  private isOwn(chunk: ChunkRef, column: number, row: number): boolean {
    const side = FOG_CELL_TILES * MAP_SCALE;
    const span = this.config.chunkSize * MAP_SCALE;
    return column * side >= chunk.cx * span && column * side < (chunk.cx + 1) * span && row * side >= chunk.cy * span && row * side < (chunk.cy + 1) * span;
  }

  /**
   * Composes the cells that sit over a chunk already drawn.
   *
   * @param cells The cells.
   */
  private compose(cells: readonly FogCell[]): void {
    if (cells.length === 0 || this.shown === null) {
      return;
    }
    this.paint(cells.filter(([column, row]) => this.coversDrawn(column, row)));
  }

  /**
   * Whether a chunk the cell covers is drawn.
   *
   * @param column The cell's column.
   * @param row The cell's row.
   * @returns True when any chunk it covers is drawn.
   */
  private coversDrawn(column: number, row: number): boolean {
    const side = FOG_CELL_TILES * MAP_SCALE;
    const span = this.config.chunkSize * MAP_SCALE;
    const x = column * side;
    const y = row * side;
    for (let cy = Math.floor(y / span); cy <= Math.floor((y + side - 1) / span); cy += 1) {
      for (let cx = Math.floor(x / span); cx <= Math.floor((x + side - 1) / span); cx += 1) {
        if (this.drawn.has(cy * this.chunksWide + cx)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Paints cells onto the display.
   *
   * @param cells The cells.
   */
  private paint(cells: readonly FogCell[]): void {
    const shown = this.shown;
    const ground = this.ground;
    if (shown === null || ground === null || cells.length === 0) {
      return;
    }
    for (const [column, row] of cells) {
      paintFogCell(shown.context, ground.source, this.held, column, row, this.config.map, MAP_SCALE, this.blur);
    }
    this.changes += 1;
  }
}
